'use client';

import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';

import { MOTION_POSITION_SMALL } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// ProgressDots — multi-step progress indicator. A row of dots with a single
// horizontal bar that fills behind them as the caller advances `currentStep`.
//
// Ported from the mobile primitive (mobile/src/components/ui/progress-dots.tsx)
// used by the gift + withdraw flows. Geometry constants (DOT_SIZE /
// PROGRESS_PADDING / PROGRESS_SIZE) are kept verbatim so the two platforms
// match pixel-for-pixel. Mobile drives the bar width with Reanimated
// `withTiming` and the dot overlay opacity with EaseView; here both run on
// Motion with `MOTION_POSITION_SMALL` (150 ms, ease-position) — the same curve +
// duration, so the feel is identical.
//
// Reduced motion: snaps both the bar width and the dot overlay to their final
// values (no transition), matching the mobile reduce-motion fallback.
// ---------------------------------------------------------------------------

const DOT_SIZE = 8;
const PROGRESS_PADDING = 8;
const PROGRESS_SIZE: number = DOT_SIZE + PROGRESS_PADDING * 2; // 24px cell (dot + padding)

export interface ProgressDotsProps {
  /**
   * Zero-based active step. Clamped to `[0, totalSteps − 1]` — being on the
   * first step (0) always reads as "step 1 of N" with one dot lit, never
   * empty. Pass anything ≥ `totalSteps` to render fully complete.
   */
  currentStep: number;
  /** Total number of steps. */
  totalSteps: number;
  /** Accessible name for the progress control. */
  ariaLabel?: string | undefined;
  className?: string | undefined;
  /** Class merged onto the filling bar (default: `bg-feedback-success-accent`). */
  barClassName?: string;
  /** Class merged onto each active dot overlay (default: `bg-white`). */
  activeDotClassName?: string;
  /** Class merged onto each inactive dot (default: `bg-background-tertiary`). */
  inactiveDotClassName?: string;
}

export function ProgressDots({
  currentStep,
  totalSteps,
  ariaLabel,
  className,
  barClassName,
  activeDotClassName,
  inactiveDotClassName,
}: ProgressDotsProps): React.ReactNode {
  const reduce = useReducedMotion() === true;

  // Clamp so out-of-range inputs render gracefully. Floor is 0 (not -1): being
  // on the first step always lights at least one dot — the user is *on* step 1,
  // so step 1 reads as in-progress. Ceiling is totalSteps − 1 (fully filled).
  const clampedStep = totalSteps > 0 ? Math.max(0, Math.min(currentStep, totalSteps - 1)) : 0;
  const progressRatio = totalSteps > 0 ? (clampedStep + 1) / totalSteps : 0;

  // Dots are positional and identical — they have no intrinsic id. Derive
  // per-position keys keyed on totalSteps so the React Compiler can memoize.
  const dotKeys = Array.from({ length: totalSteps }, (_, i) => `progress-dot-${totalSteps}-${i}`);

  return (
    <LazyMotion features={domAnimation}>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={totalSteps}
        aria-valuenow={totalSteps > 0 ? clampedStep + 1 : 0}
        aria-label={ariaLabel}
        className={cn('relative flex flex-row items-center justify-center', className)}
        style={{ width: PROGRESS_SIZE * totalSteps, height: PROGRESS_SIZE }}
      >
        <Bar
          progressRatio={progressRatio}
          totalWidth={PROGRESS_SIZE * totalSteps}
          reduce={reduce}
          className={barClassName}
        />
        {dotKeys.map((key, index) => (
          <Dot
            key={key}
            isActive={index <= clampedStep}
            reduce={reduce}
            activeClassName={activeDotClassName}
            inactiveClassName={inactiveDotClassName}
          />
        ))}
      </div>
    </LazyMotion>
  );
}

function Bar({
  progressRatio,
  totalWidth,
  reduce,
  className,
}: {
  progressRatio: number;
  totalWidth: number;
  reduce: boolean;
  className?: string | undefined;
}): React.ReactNode {
  if (totalWidth === 0) return null;

  const extensionWidth = totalWidth - PROGRESS_SIZE;
  const visibleExtension = Math.max(0, progressRatio * totalWidth - PROGRESS_SIZE);
  const extensionScale = extensionWidth > 0 ? visibleExtension / extensionWidth : 0;
  const fillClassName = cn('bg-feedback-success-accent', className);

  return (
    <div className="absolute inset-0" data-testid="progress-dots-bar">
      <div className={cn('absolute left-0 top-0 size-6 rounded-full', fillClassName)} />
      {extensionWidth > 0 && (
        <m.div
          className={cn('absolute left-3 top-0 h-full origin-left', fillClassName)}
          style={{ width: extensionWidth }}
          initial={false}
          animate={{ scaleX: extensionScale }}
          transition={reduce ? { duration: 0 } : MOTION_POSITION_SMALL}
          data-testid="progress-dots-bar-extension"
        />
      )}
      <m.div
        className={cn('absolute left-0 top-0 size-6 rounded-full', fillClassName)}
        initial={false}
        animate={{ x: visibleExtension }}
        transition={reduce ? { duration: 0 } : MOTION_POSITION_SMALL}
        data-testid="progress-dots-bar-cap"
      />
    </div>
  );
}

function Dot({
  isActive,
  reduce,
  activeClassName,
  inactiveClassName,
}: {
  isActive: boolean;
  reduce: boolean;
  activeClassName?: string | undefined;
  inactiveClassName?: string | undefined;
}): React.ReactNode {
  return (
    // `relative` keeps the cell painted above the absolutely-positioned bar.
    <div className="relative flex size-6 items-center justify-center">
      <div className={cn('size-2 rounded-full bg-background-tertiary', inactiveClassName)} />
      <m.span
        aria-hidden="true"
        className={cn('absolute size-2 rounded-full bg-white', activeClassName)}
        initial={false}
        animate={{ opacity: isActive ? 1 : 0 }}
        transition={reduce ? { duration: 0 } : MOTION_POSITION_SMALL}
      />
    </div>
  );
}
